import { useMemo } from 'react';
import type { VarianceEntry } from '../../services/analytics';
import { ChartEmpty, formatDate, useMeasure, useTooltip } from './chartPrimitives';

interface SlipWaterfallProps {
  entries: VarianceEntry[];
  limit?: number;
  onSelectActivity?: (activityId: string) => void;
}

/**
 * Activities ranked by finish-date variance against baseline, as diverging
 * bars from a zero axis. Slip right in red, recovery left in green.
 *
 * Ranked rather than listed: the question a reviewer actually has is "what
 * moved most", and a sorted bar chart answers it in one glance where a list
 * of change cards does not.
 */
export function SlipWaterfall({
  entries,
  limit = 18,
  onSelectActivity,
}: SlipWaterfallProps) {
  const [ref, { width }] = useMeasure<HTMLDivElement>();
  const tooltip = useTooltip();

  const shown = useMemo(
    () => entries.filter((e) => e.slipDays !== 0).slice(0, limit),
    [entries, limit]
  );

  if (shown.length === 0) {
    return <ChartEmpty message="No finish-date movement against the baseline." />;
  }

  const rowHeight = 22;
  const margin = { top: 22, right: 54, bottom: 8, left: 168 };
  const height = shown.length * rowHeight + margin.top + margin.bottom;
  const plotWidth = Math.max(0, width - margin.left - margin.right);

  const maxAbs = Math.max(...shown.map((e) => Math.abs(e.slipDays)), 1);
  const zeroX = plotWidth / 2;
  const scale = (days: number) => (days / maxAbs) * (plotWidth / 2);

  const truncate = (text: string, max: number) =>
    text.length > max ? `${text.slice(0, max - 1)}…` : text;

  return (
    <div ref={ref} className="w-full">
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label="Baseline finish variance">
          <g transform={`translate(${margin.left},${margin.top})`}>
            <text x={zeroX - 8} y={-9} textAnchor="end" className="chart-axis-label">
              ← EARLIER
            </text>
            <text x={zeroX + 8} y={-9} className="chart-axis-label">
              LATER →
            </text>

            <line
              x1={zeroX}
              x2={zeroX}
              y1={-4}
              y2={shown.length * rowHeight}
              stroke="var(--border-strong)"
              strokeWidth="1"
            />

            {shown.map((entry, index) => {
              const y = index * rowHeight;
              const barWidth = Math.abs(scale(entry.slipDays));
              const isSlip = entry.slipDays > 0;
              const x = isSlip ? zeroX : zeroX - barWidth;
              const colour = isSlip ? 'var(--status-fail)' : 'var(--status-pass)';

              return (
                <g
                  key={entry.activityId}
                  className="chart-hit"
                  onMouseMove={(event) =>
                    tooltip.show(
                      <div>
                        <div className="font-semibold">{entry.code}</div>
                        <div style={{ color: 'var(--text-secondary)' }}>{entry.name}</div>
                        <div className="tnum" style={{ marginTop: 4 }}>
                          Baseline: {formatDate(entry.baselineFinish)}
                        </div>
                        <div className="tnum">
                          Current: {formatDate(entry.currentFinish)}
                        </div>
                        <div className="tnum" style={{ color: colour, marginTop: 4 }}>
                          {isSlip ? '+' : ''}
                          {entry.slipDays} days
                        </div>
                      </div>,
                      event
                    )
                  }
                  onMouseLeave={tooltip.hide}
                  onClick={() => onSelectActivity?.(entry.activityId)}
                >
                  <rect
                    x={-margin.left}
                    y={y}
                    width={width}
                    height={rowHeight}
                    fill="transparent"
                  />
                  <text
                    x={-margin.left + 4}
                    y={y + rowHeight / 2 + 3.5}
                    className="chart-axis-label"
                    style={{ fill: 'var(--text-secondary)' }}
                  >
                    {truncate(`${entry.code} ${entry.name}`, 30)}
                  </text>
                  <rect
                    x={x}
                    y={y + 4}
                    width={Math.max(1.5, barWidth)}
                    height={rowHeight - 8}
                    fill={colour}
                    rx={1}
                    opacity={entry.isCritical ? 1 : 0.62}
                  />
                  <text
                    x={isSlip ? x + barWidth + 6 : x - 6}
                    y={y + rowHeight / 2 + 3.5}
                    textAnchor={isSlip ? 'start' : 'end'}
                    className="chart-axis-label tnum"
                    style={{ fill: colour }}
                  >
                    {isSlip ? '+' : ''}
                    {entry.slipDays}d
                  </text>
                </g>
              );
            })}
          </g>
        </svg>
      )}

      <p className="mt-2 text-xs" style={{ color: 'var(--text-muted)' }}>
        Showing the {shown.length} largest movements of {entries.filter((e) => e.slipDays !== 0).length}.
        Solid bars are on the critical path.
      </p>
    </div>
  );
}
